import { createSelector } from '@reduxjs/toolkit';
import Cookies from 'cookies-js';
import i18next from 'i18next';
import { responseStatus } from '../shared/statuses';

import { COOKIE_CENTRAL_STORAGE } from '../actions/retailer';

import type {
  SubscriptionAdvertisement,
  SubscriptionSearchItem,
  SubscriptionSearchListItem,
} from '../@types/AdvertismentSubscription';
import type { Facet, Filter, Page, SortOption } from '../@types/ElasticSearchResponse';
import type { ElasticVehicle } from '../@types/ElasticVehicle';
import type { Faq } from '../@types/Faq';
import type { Branding } from '../@types/branding';
import type Item from '../@types/item';
import QueryFilter from '../helpers/query-filter';
import { slugify } from '../helpers/urls';
import type { ParsedList } from '../models/list';
import type { RootState } from '../reducers';
import { getList } from '../selectors/list';
import { getDistance } from './location';
import { getLocationSet } from './maps';
import { getHiddenFilters } from './market';

const MANUFACTURER_PACKAGE_CATEGORY = 'Manufacture packaging';

export const getVehicleLookupResult = (state: RootState) => state.vehicles.current;

export const getQueryFilter = createSelector(
  (state: RootState) => state.search.queryFilter || '',
  (queryFilter): QueryFilter => new QueryFilter(queryFilter),
);

const getFacetStructure = (): { [key: string]: FacetListId | undefined } => ({
  location: {
    sortOrder: 0,
    displayName: i18next.t('filter.map', { ns: 'search' }),
  },
  manufacturer: {
    sortOrder: 1,
    displayName: i18next.t('filter.manufacturer', { ns: 'search' }),
  },
  modelSeries: {
    sortOrder: 2,
    displayName: i18next.t('filter.model', { ns: 'search' }),
  },
  modelYear: {
    sortOrder: 3,
    isRangeFacet: true,
    valueFormat: false,
    displayName: i18next.t('filter.modelYear.displayName', { ns: 'search' }),
    prefix: i18next.t('filter.modelYear.prefix', { ns: 'search' }),
    suffix: '',
    histogram: true,
    rangeStep: 1,
  },
  price: {
    sortOrder: 4,
    isRangeFacet: true,
    valueFormat: true,
    displayName: i18next.t('filter.price.displayName', { ns: 'search' }),
    prefix: '',
    suffix: i18next.t('filter.price.unit', { ns: 'search' }),
    histogram: true,
    rangeStep: 10000,
    disabled: false,
  },
  leasingPrice: {
    sortOrder: 5,
    isRangeFacet: true,
    valueFormat: true,
    displayName: i18next.t('filter.leasingPrice.displayName', { ns: 'search' }),
    prefix: '',
    suffix: i18next.t('filter.leasingPrice.unit', { ns: 'search' }),
    histogram: true,
    rangeStep: 500,
    disabled: true,
  },
  businessLeasingPrice: {
    sortOrder: 6,
    isRangeFacet: true,
    valueFormat: true,
    displayName: i18next.t('filter.businessLeasingPrice.displayName', { ns: 'search' }),
    prefix: '',
    suffix: i18next.t('filter.businessLeasingPrice.unit', { ns: 'search' }),
    histogram: true,
    rangeStep: 500,
    disabled: true,
  },
  gearboxType: {
    sortOrder: 7,
    displayName: i18next.t('filter.gearbox', { ns: 'search' }),
  },
  mileage: {
    sortOrder: 8,
    isRangeFacet: true,
    valueFormat: true,
    displayName: i18next.t('filter.odometer.displayName', { ns: 'search' }),
    prefix: '',
    suffix: i18next.t('filter.odometer.unitScandinavianMile', { ns: 'search', count: 2 }),
    suffixSingular: i18next.t('filter.odometer.unitScandinavianMile', { ns: 'search', count: 1 }),
    histogram: true,
    rangeStep: 100,
  },
  odometerValueAsKm: {
    sortOrder: 9,
    isRangeFacet: true,
    valueFormat: true,
    displayName: i18next.t('filter.odometer.displayName', { ns: 'search' }),
    prefix: '',
    suffix: i18next.t('filter.odometer.unitKilometer', { ns: 'search', count: 2 }),
    suffixSingular: i18next.t('filter.odometer.unitKilometer', { ns: 'search', count: 1 }),
    histogram: true,
    rangeStep: 1000,
  },

  fuelTypes: {
    sortOrder: 10,
    displayName: i18next.t('filter.fuelType', { ns: 'search' }),
  },
  engineBaseType: {
    sortOrder: 11,
    displayName: i18next.t('filter.engineBaseType', { ns: 'search' }),
  },
  fuelType: {
    sortOrder: 12,
    displayName: i18next.t('filter.fuelType', { ns: 'search' }),
  },
  enginePower: {
    sortOrder: 13,
    isRangeFacet: true,
    valueFormat: false,
    displayName: i18next.t('filter.enginePower.displayName', { ns: 'search' }),
    prefix: '',
    suffix: i18next.t('filter.enginePower.unit', { ns: 'search' }),
    histogram: true,
    rangeStep: 25,
  },
  'enhancedProperties.electricalRangeWLTP.value': {
    sortOrder: 14,
    displayName: i18next.t('filter.electricalRange.displayName', { ns: 'search' }),
    isRangeFacet: true,
    valueFormat: true,
    suffix: i18next.t('filter.electricalRange.unit', { ns: 'search' }),
    prefix: i18next.t('filter.electricalRange.displayName', { ns: 'search' }),
    histogram: true,
    rangeStep: 10,
  },
  chassis: {
    sortOrder: 15,
    displayName: i18next.t('filter.chassis', { ns: 'search' }),
  },
  drivingWheel: {
    sortOrder: 16,
    displayName: i18next.t('filter.drivingWheel', { ns: 'search' }),
  },
  'properties.segment': {
    sortOrder: 17,
    displayName: i18next.t('filter.segment', { ns: 'search' }),
  },
  color: {
    sortOrder: 18,
    displayName: i18next.t('filter.color', { ns: 'search' }),
  },
  'properties.seats': {
    sortOrder: 19,
    isRangeFacet: true,
    valueFormat: false,
    displayName: i18next.t('filter.seats.displayName', { ns: 'search' }),
    prefix: '',
    suffix: i18next.t('filter.seats.unit', { ns: 'search', count: 2 }),
    suffixSingular: i18next.t('filter.seats.unit', { ns: 'search', count: 1 }),
    histogram: false,
    rangeStep: 1,
  },
  environmentClass: {
    sortOrder: 20,
    displayName: i18next.t('filter.environmentClass', { ns: 'search' }),
  },
  branches: {
    sortOrder: 21,
    displayName: i18next.t('filter.branches', { ns: 'search' }),
    isExcludeProhibited: true,
  },
  'properties.co2': {
    sortOrder: 22,
    isRangeFacet: true,
    valueFormat: true,
    displayName: i18next.t('filter.co2.displayName', { ns: 'search' }),
    prefix: '',
    suffix: i18next.t('filter.co2.unit', { ns: 'search' }),
    histogram: true,
    rangeStep: 10,
  },
  inventoryStatus: {
    sortOrder: 23,
    displayName: i18next.t('filter.inventoryStatus.displayName', { ns: 'search' }),
  },
});

interface FacetListId {
  sortOrder: number;
  displayName: string;
  isRangeFacet?: boolean;
  valueFormat?: boolean;
  prefix?: string;
  title?: string | null;
  suffix?: string;
  histogram?: boolean;
  rangeStep?: number;
  disabled?: boolean;
  suffixSingular?: string;
  prefixChar?: string;
  suffixChar?: string;
  isExcludeProhibited?: boolean;
  isLocationFacet?: boolean;
  selectionCount?: number;
  min?: number;
  max?: number;
  range?: {
    min: number;
    max: number;
  };
  contextual?: {
    filters?: Filter[];
  };
  hidden?: boolean;
}

export type CombinedFacet = Facet & FacetListId;

const translateFacetFilters = (facets: Facet[]) =>
  facets.map((x) => ({
    ...x,
    filters: x.filters?.map((y) => ({
      ...y,
      translatedDisplayName:
        x.id === 'inventoryStatus'
          ? i18next.t(`filter.inventoryStatus.options.${y.displayName}`, { ns: 'search' })
          : y.displayName,
    })),
  }));

const facetsAggregator = (_facets: Facet[], _list: Facet[], queryFilter: QueryFilter) => {
  const facets = translateFacetFilters(_facets);
  const list = translateFacetFilters(_list);

  const result: CombinedFacet[] = [];
  const position = queryFilter.getSpatial();
  const hiddenFilters = getHiddenFilters();
  const facetStructure = getFacetStructure();
  const loc = {
    ...(facetStructure.location as FacetListId),
    id: 'location',
    isRangeFacet: false,
    isLocationFacet: true,
    selectionCount: queryFilter.hasSpatial() ? 1 : 0,
    title: queryFilter.hasSpatial() ? `${position?.name} +${position?.distance}km` : null,
    filters: [
      ...[
        queryFilter.hasSpatial()
          ? { ...position, title: position?.name, displayName: position?.name, selected: true }
          : null,
      ],
      ...getLocationSet()
        .filter((y) => y.name !== position?.name)
        .map((x) => ({
          displayName: x.name,
          distance: 100,
          lat: x.lat,
          lng: x.lng,
          name: x.name,
          selected: false,
          title: x.name,
        })),
    ].filter((p) => !!p) as unknown as Filter[],
  };

  result[loc.sortOrder] = loc;

  for (let i = 0; i < list.length; i++) {
    const facet = facetStructure[list[i].id];
    const latest = facets.find((f) => f.id === list[i].id);

    if (!facet) {
      continue;
    }

    const hidden = hiddenFilters.includes(list[i].id);

    if (facet.isRangeFacet) {
      const filterMin =
        list[i].filters && list[i].filters.length ? list[i].filters[0].displayName : '0';
      const filterMax =
        list[i].filters && list[i].filters.length
          ? list[i].filters[list[i].filters.length - 1].displayName
          : '0';
      const min = queryFilter.hasRangeMin(list[i].id)
        ? queryFilter.getRangeMin(list[i].id)
        : filterMin;
      const max = queryFilter.hasRangeMax(list[i].id)
        ? queryFilter.getRangeMax(list[i].id)
        : filterMax;
      let prefixChar = min?.replace(/^([\+<>\-])[\.,0-9]+$/, '$1');
      let suffixChar = max?.replace(/^[\.,0-9]+([\+<>\-])$/, '$1');
      const baseMinNum = Number.parseInt(filterMin.replace(/[^\.,0-9]/g, ''), 10);
      const baseMaxNum = Number.parseInt(filterMax.replace(/^\.,0-9]/g, ''), 10);
      const minNum = Number.parseInt((min !== undefined ? min : '').replace(/[^\.,0-9]/g, ''), 10);
      const maxNum = Number.parseInt((max !== undefined ? max : '').replace(/[^\.,0-9]/g, ''), 10);

      if (prefixChar === min) {
        prefixChar = '';
      }
      if (suffixChar === max) {
        suffixChar = '';
      }

      let disabled = false;
      if (
        list[i].id === 'leasingPrice' &&
        !queryFilter.hasRange('leasingPrice') &&
        !queryFilter.has('leasing')
      ) {
        disabled = true;
      }
      if (
        list[i].id === 'businessLeasingPrice' &&
        !queryFilter.hasRange('businessLeasingPrice') &&
        !queryFilter.has('businessLeasing')
      ) {
        disabled = true;
      }
      if (
        list[i].id === 'price' &&
        (queryFilter.hasRange('leasingPrice') ||
          queryFilter.has('leasing') ||
          queryFilter.hasRange('businessLeasingPrice') ||
          queryFilter.has('businessLeasing'))
      ) {
        disabled = true;
      }

      let rangeTitle: string | null = null;
      if (minNum !== baseMinNum || maxNum !== baseMaxNum) {
        rangeTitle = `${facet.prefix ? `${facet.prefix} ` : ''}${prefixChar}${minNum}-${maxNum}${suffixChar}${
          facet.suffix ? ` ${facet.suffix}` : ''
        }`;
      }
      result[facet.sortOrder] = {
        ...list[i],
        contextual: {
          filters: latest?.filters,
        },
        sortOrder: facet.sortOrder,
        isRangeFacet: true,
        isLocationFacet: false,
        displayName: facet.displayName,
        title: rangeTitle,
        valueFormat: facet.valueFormat,
        histogram: facet.histogram,
        rangeStep: facet.rangeStep,
        selectionCount: queryFilter.hasRange(list[i].id) ? 1 : 0,
        prefix: facet.prefix && `${facet.prefix} `,
        suffixSingular: facet.suffixSingular,
        prefixChar,
        suffix: facet.suffix && ` ${facet.suffix}`,
        suffixChar,
        min: baseMinNum,
        max: baseMaxNum,
        range: { min: minNum, max: maxNum },
        disabled,
        hidden,
      };
    } else {
      let selectionCount = 0;
      let title: string | null = null;
      const filters: Filter[] = [];

      for (let j = 0; j < (list[i].filters || []).length; j++) {
        const filter = (latest?.filters || []).find(
          (f) => f.displayName === list[i].filters[j].displayName,
        );
        let next: Filter | null = null;
        if (filter) {
          next = { ...filter };
        } else {
          next = {
            ...list[i].filters[j],
            selected: false,
            count: queryFilter.isEmpty() ? list[i].filters[j].count : 0,
          };
        }
        if (filter && filter.selected) {
          title = selectionCount === 0 ? filter.displayName : title;
          selectionCount += 1;
        }
        filters.push(next);
      }

      (latest?.filters || [])
        .filter((x) => x.selected && !filters.find((z) => z.displayName === x.displayName))
        .forEach((y) => {
          title = selectionCount === 0 ? y.displayName : title;
          selectionCount += 1;
          filters.push({ ...y, selected: true, count: 0 });
        });
      result[facet.sortOrder] = {
        ...list[i],
        contextual: {
          filters: latest?.filters,
        },
        isRangeFacet: false,
        sortOrder: facet.sortOrder,
        isLocationFacet: false,
        filters: filters
          ?.sort((a, b) => {
            if (a.selected && !b.selected) {
              return -1;
            }
            if (b.selected && !a.selected) {
              return 1;
            }

            return b.count - a.count;
          })
          .map((f) => (selectionCount > 0 ? f : { ...f, selected: false })),
        displayName: facet.displayName,
        selectionCount,
        title: selectionCount > 1 ? `${title} +${selectionCount - 1}` : title,
        isExcludeProhibited: facet.isExcludeProhibited,
        hidden,
      };
    }
  }

  return result.filter((r) => !r.disabled);
};

const emptyFacets: Facet[] = [];
const emptyFacetList: Facet[] = [];

export const getFacetList = createSelector(
  (state: RootState) => state.search.facets || emptyFacets,
  (state: RootState) => state.search.facetList || emptyFacetList,
  getQueryFilter,
  facetsAggregator,
);
const getQueryURLFacetValues = (pathStartsWith: string) => {
  if (window.location.pathname.substr(0, pathStartsWith.length) !== pathStartsWith) return [];

  const queryURL = window.location.pathname.substr(pathStartsWith.length);
  return queryURL
    .replace(/^\/|\/$/g, '')
    .split('/')
    .filter((q) => !!q);
};

export const getQueryFilterFromQueryURL = createSelector(
  getFacetList,
  (_: RootState, pathStartsWith: string) =>
    process.env.SSR ? [] : getQueryURLFacetValues(pathStartsWith),
  (facets, queryURLValues) => {
    const _queryURLValues = [...queryURLValues];
    if (_queryURLValues.length === 0) return null;
    let queryFilter = new QueryFilter();
    facets.forEach((facet) =>
      facet?.filters?.forEach((filter) => {
        const index = filter.displayName
          ? _queryURLValues.indexOf(slugify(filter.displayName))
          : -1;
        if (index > -1) {
          if (!facet.isRangeFacet) {
            queryFilter = queryFilter.add(facet.id, filter.displayName);
          }
          /** Removes matched queryUrlFacetValue
           *  Example: sok/mini will both match manufacturer MINI and modelSeries Mini causing zero results
           *  because no car has manufacture MINI and modelSeries Mini.
           *  ModelSeries mini is related to manufacturer Ausin (Old and before BMW bought Austin)
           **/
          _queryURLValues.splice(index, 1);
        }
      }),
    );

    if (queryFilter.isEmpty()) return null;

    return queryFilter;
  },
);

export const getActiveFacet = createSelector(
  (state: RootState) => state.filters.facet,
  getFacetList,
  (current, facets) => (current ? (facets || []).find((f) => f && f.id === current.id) : null),
);

export const getFacetSelections = createSelector(
  getFacetList,
  getQueryFilter,
  (facets, queryFilter) => {
    const facetStructure = getFacetStructure();
    return (facets || [])
      .map(
        (facet): CombinedFacet => ({
          ...facet,
          filters: (facet.filters || []).filter(
            (filter) =>
              (filter.selected &&
                (queryFilter.contains(facet.id, filter.displayName) || facet.isLocationFacet)) ||
              queryFilter.hasRange(facet.id),
          ),
        }),
      )
      .filter((facet) => facet.filters.length > 0)
      .sort(
        (a, b) => (facetStructure[a.id]?.sortOrder || 0) - (facetStructure[b.id]?.sortOrder || 0),
      );
  },
);

export const getExcludingFacetSelections = createSelector(
  getFacetList,
  getQueryFilter,
  (facets, queryFilter) => {
    const facetStructure = getFacetStructure();
    return (facets || [])
      .map(
        (facet): CombinedFacet => ({
          ...facet,
          filters: (facet.filters || []).filter(
            (filter) => filter.selected && queryFilter.excludes(facet.id, filter.displayName),
          ),
        }),
      )
      .filter((facet) => facet.filters.length > 0)
      .sort(
        (a, b) => (facetStructure[a.id]?.sortOrder || 0) - (facetStructure[b.id]?.sortOrder || 0),
      );
  },
);

const emptySearchResultSet: ElasticVehicle[] = [];

export const getSearchResultSet = createSelector(
  (state: RootState) => state.search.documentList,
  (documentList) => (documentList ? documentList.documents : emptySearchResultSet),
);

const emptySearchSorting: SortOption[] = [];

export const getSearchSorting = createSelector(
  (state: RootState) => state.search.documentList,
  (documentList) => (documentList ? documentList.sortOptions : emptySearchSorting),
);

export const getSearchHitCount = createSelector(
  (state: RootState) => state.search.queryFilter,
  (state: RootState) => state.search.documentList,
  (state: RootState) => state.search.hitCount,
  (queryFilter, documentList, hitCount) => {
    if (queryFilter && documentList) {
      return documentList.numberOfHits;
    }

    return hitCount;
  },
);

const emptySearchPages: Page[] = [];

export const getSearchPages = createSelector(
  (state: RootState) => state.search.documentList,
  (documentList) =>
    documentList && documentList.pagination ? documentList.pagination.pages : emptySearchPages,
);

export const getSearchDocument = createSelector(
  (state: RootState) => state.item.current,
  (documentList) =>
    documentList && documentList.documents && documentList.documents.length === 1
      ? documentList.documents[0]
      : null,
);

export const getLinkCloudForAdvertisement = createSelector(
  (state: RootState) => state.item.facets,
  getSearchDocument,
  (facets, item) => {
    if (!facets || !item) return [];

    const facet = facets.find((f) => f.id === 'modelSeries');
    if (!facet || !facet.filters) return [];

    return facet.filters
      .filter((f) => f.count > 0)
      .map((f) => ({
        target: `/sok/${slugify(item.manufacturer)}/${slugify(f.displayName)}`,
        name: f.displayName,
        count: f.count,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  },
);

const emptySortOptions: SortOption[] = [];

export const getRetailersSorting = createSelector(
  (state: RootState) => state.retailers.documentList,
  (documentList) => (documentList ? documentList.sortOptions : emptySortOptions),
);

const emptyPagniationPages: Page[] = [];

export const getRetailersPages = createSelector(
  (state: RootState) => state.retailers.documentList,
  (documentList) =>
    documentList && documentList.pagination ? documentList.pagination.pages : emptyPagniationPages,
);

export const getRetailerDocument = createSelector(
  (state: RootState) => state.retailers.single,
  (documentList) =>
    documentList && documentList.documents && documentList.documents.length === 1
      ? documentList.documents[0]
      : null,
);

export const isRetailerUsingFreespee = createSelector(
  (state: RootState) => state.retailers.single,
  (documentList) =>
    documentList &&
    documentList.documents &&
    documentList.documents.length === 1 &&
    documentList.documents[0].telephoneNumberGenerationSettings &&
    documentList.documents[0].telephoneNumberGenerationSettings.active,
);

export const getRetailerSearch = createSelector(
  (state: RootState) => state.retailers.searchDocuments,
  (documentList) => (documentList && documentList.documents ? documentList.documents : []),
);

export const getRetailerSearchPages = createSelector(
  (state: RootState) => state.retailers.searchDocuments,
  (documentList) => (documentList && documentList.pagination ? documentList.pagination.pages : []),
);

export const getMedia = createSelector(
  (state: RootState) => state.media.connected,
  (_: RootState, ref: { id: string }) => ref.id,
  (connectedMedia, refId) => {
    if (!connectedMedia || !refId) return [];

    if (!connectedMedia[refId]) return [];

    return connectedMedia?.[refId]
      ?.filter((m) => m.files && m.files.length > 0)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((m) => m.files[0].url);
  },
);

export const getSearchHistory = createSelector(
  (state: RootState) => state.history.list,
  (history) => history || [],
);

export const getSearchSubscriptions = createSelector(
  (state: RootState) => state.subscriptions.search.list,
  (subscriptions) => (subscriptions || []).filter((s) => !s.referenceId),
);

const getSearchSubscriptionsFull = (state: RootState) => state.subscriptions.search.list;

type SearchSubscriptionsListWithCategory = SubscriptionSearchItem & {
  list?: ParsedList | null;
};

export const getSearchSubscriptionsListWithCategory = createSelector(
  (state: RootState) => state.subscriptions.search.list,
  getList,
  (subscriptions, list): SearchSubscriptionsListWithCategory[] => {
    return subscriptions.map((item) => {
      if (item.referenceId) {
        const match = list.find((x) => x.id === item.referenceId);
        return {
          ...item,
          image: match ? match.smallImage : null,
          list: match ? match : null,
        };
      }
      return item;
    });
  },
);

export const getSearchSubscriptionsListWithCategoryNewHits = (state: RootState) => {
  const list = getSearchSubscriptionsListWithCategory(state);
  return list.reduce((sum, next) => sum + (next?.numberOfHits || 0), 0);
};

export const getLastSearchSubscription = (state: RootState) => {
  const subscriptions = getSearchSubscriptions(state);
  if (subscriptions.length) return subscriptions[subscriptions.length - 1];
  return null;
};

export const getSearchSubscription = (state: RootState, id?: string | null) => {
  if (!id) return null;
  const subscriptions = getSearchSubscriptions(state);
  const match = subscriptions.find((x) => x.id === id);
  return match ? match : null;
};

export const getPrenumerationSubscriptions = createSelector(
  (state: RootState) => state.subscriptions.search.list,
  getList,
  (subscriptions, list): SubscriptionSearchListItem[] => {
    return (subscriptions || [])
      .filter((s) => !!s.referenceId)
      .map((item) => {
        const match = list.find((x) => x.id === item.referenceId);
        return {
          ...item,
          image: match ? match.image : null,
          list: match ? match : null,
        };
      });
  },
);

export const getPrenumerationSubscriptionByReferenceId = (state: RootState, id?: string) => {
  if (!id) return null;

  const subscriptions = getSearchSubscriptionsFull(state);
  const match = subscriptions.find((x) => x.referenceId === id);
  return match ? match : null;
};

export const isAdvertiesementSubscriptionsSynced = (state: RootState) =>
  state.subscriptions.advertisement.synced;

export const getAdvertisementSubscriptions = (state: RootState) =>
  state.subscriptions.advertisement.list;

export const getAdvertisementSubscription = createSelector(
  (state: RootState) => state.subscriptions.advertisement.list,
  (_: RootState, item?: ElasticVehicle | Item | null) => item,
  (subscriptions, item) => {
    if (!item || subscriptions.length === 0) {
      return null;
    }

    return subscriptions.find((s) => s.advertisementId === item.id);
  },
);

export const getSharedNamespaceAdvertisements = (state: RootState) =>
  state.sharedUserContent.sharedNamespaceAdvertisements;

const emptyGuid = '00000000-0000-0000-0000-000000000000';
export type ComboSubscription = Omit<SubscriptionAdvertisement, 'namespace'> & {
  namespace?: {
    name: string;
    id: string;
  };
};

const NamespaceFilter = (a: ComboSubscription, id: string) => {
  if (a.id === emptyGuid) {
    return true;
  }
  return !!a.namespace && a.namespace.id === id;
};

const namespacePreMap = (a: SubscriptionAdvertisement): ComboSubscription => {
  if (!a.namespace) {
    return {
      ...a,
      namespace: {
        name: i18next.t('unknown', { ns: 'validation' }),
        id: emptyGuid,
      },
    };
  }
  return a;
};

export const getAdvertisementsForNamespace = createSelector(
  (state: RootState) => state.subscriptions.advertisement.list,
  (_: RootState, id: string) => id,
  (list, id) => list.map(namespacePreMap).filter((a) => NamespaceFilter(a, id)),
);

export const getAdvertisementNamespaceFromAdvertisements = (state: RootState, id: string) =>
  state.subscriptions.advertisement.list
    .map(namespacePreMap)
    .filter((a) => NamespaceFilter(a, id))
    .map((a) => a.namespace)
    .pop();

export const getAdvertisementNamespace = (state: RootState, id: string) =>
  state.subscriptions.namespaces.list.filter((a) => a.id === id).pop();

export const getSharedAdvertisementNamespace = (state: RootState, id: string) =>
  state.sharedUserContent.sharedNamespaceAdvertisements
    .filter((a) => !!a.namespace && a.namespace.id === id)
    .map((a) => a.namespace)
    .pop();

export const getSettings = (state: RootState) => state.settings.current;

const emptyBrandingsList: Branding[] = [];

export const shouldFetchBrandings = createSelector(
  (state: RootState) => state.brandings.list || emptyBrandingsList,
  (brandings) => brandings.length === 0,
);

export const getCreditCheckApiIdentifier = createSelector(
  (state: RootState) => state.brandings.list || [],
  (state: RootState) =>
    state.item.current && state.item.current.documents && state.item.current.documents.length === 1
      ? state.item.current.documents[0]
      : null,
  (brandings, item) => {
    if (!item || !item.financialOptions || !item.financialOptions.length) {
      return null;
    }

    const branding = brandings.find(
      (b) => b.name === item.financialOptions[0].financialInstitution,
    );

    if (branding && branding.creditCheckApiIdentifier) {
      return branding.creditCheckApiIdentifier;
    }
    return null;
  },
);

const empty: Branding[] = [];
export const getManufacturerPackagings = createSelector(
  (state: RootState) => state.brandings.list || empty,
  (packagings) => packagings.filter((p) => p.category.includes(MANUFACTURER_PACKAGE_CATEGORY)),
);

export const getItemManufacturerPackagings = createSelector(
  (state: RootState) =>
    (state.brandings.list || []).filter((b) => b.category.includes(MANUFACTURER_PACKAGE_CATEGORY)),
  (_: RootState, item: Item) => item,
  (packagings, item) => {
    if (!item || !item.hasManufacturerPackaging) {
      return [];
    }

    return packagings.filter((p) => p.identifier === item.manufacturer);
  },
);

export const getFilterManufacturerPackaging = createSelector(
  getQueryFilter,
  getManufacturerPackagings,
  (qF, brandingList) => {
    const manufacturers = qF.get('manufacturer');
    if (manufacturers && manufacturers.length === 1 && !qF.has('hasManufacturerPackaging')) {
      const mP = brandingList.find((x) => x.identifier === manufacturers[0]);
      return mP ? mP : null;
    }

    return null;
  },
);

export const getItemResellerPackagings = createSelector(
  (state: RootState) =>
    state.retailers.single && state.retailers.single.documents.length === 1
      ? state.retailers.single.documents[0]
      : null,
  (_: RootState, item: Item | null) => item,
  (retailer, item) => {
    if (!retailer) {
      return [];
    }

    const identifiers =
      item && item.resellerPackagingOptions
        ? item.resellerPackagingOptions.map((rpo) => rpo.id)
        : [];
    return (retailer.resellerPackagingOptions || []).filter((rpo) => identifiers.includes(rpo.id));
  },
);

export const isErrorPage = createSelector(
  (state: RootState) => state.routing.isErrorPage,
  (state: RootState) => state.search.notFound,
  (state: RootState) => state.contentpages.responseStatus === responseStatus.FAILED,
  (state: RootState, slug?: string) =>
    state.faq.categories && slug && !state.faq.categories.find((c) => c.slug === slug),
  (state: RootState) => state.item.notFound,
  (
    errorFromServer,
    isMissingSearchDocument,
    isMissingContentPage,
    isMissingFaqPage,
    isMissingSearchDocumentItem,
  ) =>
    !!(
      errorFromServer ||
      isMissingSearchDocument ||
      isMissingContentPage ||
      isMissingFaqPage ||
      isMissingSearchDocumentItem
    ),
);

const emptyFaqList: Faq[] = [];

export const getFAQs = createSelector(
  (state: RootState) => state.faq.list,
  (list) => list || emptyFaqList,
);

export const getContentModules = createSelector(
  (state: RootState) => state.contentmodules.documentList,
  (documentList) => {
    const list =
      documentList && documentList.documents && documentList.documents.length
        ? documentList.documents
        : [];
    return list.sort((a, b) => a.sortOrder - b.sortOrder);
  },
);

export const getUploadedMedia = (state: RootState) => state.media.uploads;

export const getPipeMessage = (state: RootState) => state.events.lastPipeMessage;

export const getSafetyAddonsConversation = createSelector(
  (state: RootState) => state.safetyAddons.list,
  (list) =>
    list && list.documentList && list.documentList.documents ? list.documentList.documents : null,
);

const getCentralStorageId = () => {
  if (!process.env.SSR) {
    return Cookies.get(COOKIE_CENTRAL_STORAGE);
  }

  return null;
};

const getCentralStorageRetailers = createSelector(getSearchDocument, (item) =>
  item &&
  item.branches &&
  item.branches.length &&
  item.branches[0].connectedBranches &&
  item.branches[0].connectedBranches.length
    ? item.branches[0].connectedBranches
    : null,
);

export interface GetGeoLocation {
  lat: number;
  lng: number;
}

export const getGeoLocation = createSelector(
  (state: RootState) => state.geo.location,
  (location): GetGeoLocation | null => {
    if (location && location.lat && location.lng) {
      return {
        lat: location.lat,
        lng: location.lng,
      };
    }
    return null;
  },
);

export const getLocation = createSelector(
  getQueryFilter,
  getGeoLocation,
  (queryFilter, geoLocation) => {
    if (queryFilter.hasSpatial()) {
      return queryFilter.getSpatial();
    } else if (geoLocation) {
      return geoLocation;
    }
    return null;
  },
);

export const getSelectedCentralStorageRetailer = createSelector(
  getCentralStorageRetailers,
  getQueryFilter,
  getLocation,
  (branches, qF, loc) => {
    let branch = null;
    if (branches && branches.length) {
      if (qF.has('branches') && !!qF.get('branches').length) {
        for (let x = 0; x < qF.get('branches').length; x++) {
          for (let y = 0; y < branches.length; y++) {
            if (branches[y].name === qF.get('branches')[x]) {
              branch = branches[y];
              if (!process.env.SSR) {
                Cookies.set(COOKIE_CENTRAL_STORAGE, branch.id);
              }
              break;
            }
          }
          if (branch) {
            break;
          }
        }
      }

      const centralStorageId = getCentralStorageId();
      if (!branch && centralStorageId) {
        branch = branches.find((x) => x.id === centralStorageId);
      }

      if (!branch) {
        const uL = qF.hasSpatial() ? qF.getSpatial() : undefined;
        if (uL) {
          //If location exist
          branch = branches
            .sort(
              (a, b) =>
                getDistance(uL?.lat, uL?.lng, b.position.location.lat, b.position.location.lon) -
                getDistance(uL?.lat, uL?.lng, a.position.location.lat, a.position.location.lon),
            )
            .slice(0)
            .pop();
        } else if (loc) {
          branch = branches
            .sort(
              (a, b) =>
                getDistance(loc.lat, loc.lng, b.position.location.lat, b.position.location.lon) -
                getDistance(loc.lat, loc.lng, a.position.location.lat, a.position.location.lon),
            )
            .slice(0)
            .pop();
        } else {
          branch = branches[0];
        }
      }
    }
    return branch;
  },
);
